import { computed } from "mobx";
import { useMemo } from "react";
import { useFilterSettingsStore, usePipedriveStore } from "..";
import IDeal from "../interfaces/IDeal";
import { getMinMax } from "../utils/utils";

export default function (deals: IDeal[]): IDeal[] {
    const { filters: { dealSettings: dealFilters } } = useFilterSettingsStore();
    const pipedriveStore = usePipedriveStore();

    const filteredByHideFlags = useMemo(() => {
        return deals.filter(d =>
            (!dealFilters.hideSold || d.sold !== "Yes") &&
            (!dealFilters.hideNonFund || d.nonFund !== "Yes")
        );
    }, [deals, dealFilters.hideSold, dealFilters.hideNonFund]);

    const numberFilteredDeals = useMemo(() => {
        return filteredByHideFlags.filter(d =>
            (!dealFilters.minValue || d.value && d.value >= dealFilters.minValue) &&
            (!dealFilters.maxValue || d.value && d.value <= dealFilters.maxValue) &&
            (!dealFilters.minBuildingSize || d.buildingSize && d.buildingSize >= dealFilters.minBuildingSize) &&
            (!dealFilters.maxBuildingSize || d.buildingSize && d.buildingSize <= dealFilters.maxBuildingSize) &&
            (!dealFilters.minLandSize || d.landSizeAcres && d.landSizeAcres >= dealFilters.minLandSize) &&
            (!dealFilters.maxLandSize || d.landSizeAcres && d.landSizeAcres <= dealFilters.maxLandSize) &&
            (!dealFilters.minDateModified || d.updatedAt && d.updatedAt >= dealFilters.minDateModified) &&
            (!dealFilters.maxDateModified || d.updatedAt && d.updatedAt <= dealFilters.maxDateModified)
        );
    }, [
        filteredByHideFlags,
        dealFilters.minValue, dealFilters.maxValue,
        dealFilters.minBuildingSize, dealFilters.maxBuildingSize,
        dealFilters.minLandSize, dealFilters.maxLandSize,
        dealFilters.minDateModified, dealFilters.maxDateModified,
    ]);

    const statusStagesAndLabelsFilteredDeals = useMemo(() => {
        if (!dealFilters.statuses.length && !dealFilters.dealStageIds.length && !dealFilters.labelIds.length)
            return numberFilteredDeals;

        return numberFilteredDeals.filter(d =>
            (!dealFilters.statuses.length || dealFilters.statuses.includes(d.status)) &&
            (!dealFilters.dealStageIds.length || dealFilters.dealStageIds.includes(d.dealStageId)) &&
            (!dealFilters.labelIds.length || (d.labelId && dealFilters.labelIds.includes(d.labelId)))
        );
    }, [
        numberFilteredDeals,
        dealFilters.statuses, dealFilters.dealStageIds, dealFilters.labelIds,
    ]);

    const personFilteredDeals = useMemo(() => {
        if (!dealFilters.pipedrivePersonName)
            return statusStagesAndLabelsFilteredDeals;

        const pipedrivePersonIds = dealFilters.pipedrivePersonName ? pipedriveStore.persons.filter(p => p.name === dealFilters.pipedrivePersonName).map(p => p.id) : [];

        return statusStagesAndLabelsFilteredDeals.filter(d =>
            (d.brokerId && pipedrivePersonIds.includes(d.brokerId)) ||
            (d.contactPersonId && pipedrivePersonIds.includes(d.contactPersonId)) ||
            (d.propertyOwnerId && pipedrivePersonIds.includes(d.propertyOwnerId))
        );
    }, [statusStagesAndLabelsFilteredDeals, dealFilters.pipedrivePersonName, pipedriveStore.persons]);

    return useMemo(() => {
        if (!dealFilters.keyword)
            return personFilteredDeals;

        const keywordToLowerCase = dealFilters.keyword.toLowerCase();
        return personFilteredDeals.filter(d =>
            d.name.toLowerCase().includes(keywordToLowerCase) ||
            (d.value && d.value.toString().includes(keywordToLowerCase)) ||
            (d.status && d.status.toLowerCase().includes(keywordToLowerCase)) ||
            (d.tenant && d.tenant.toLowerCase().includes(keywordToLowerCase)) ||
            (d.contactPersonId && pipedriveStore.persons.find(p => p.id === d.contactPersonId)?.name.toLowerCase().includes(keywordToLowerCase)) ||
            (d.contactCompanyId && pipedriveStore.organizations.find(o => o.id === d.contactCompanyId)?.name.toLowerCase().includes(keywordToLowerCase)) ||
            (d.propertyOwnerId && pipedriveStore.persons.find(p => p.id === d.propertyOwnerId)?.name.toLowerCase().includes(keywordToLowerCase)) ||
            (d.brokerId && pipedriveStore.persons.find(p => p.id === d.brokerId)?.name.toLowerCase().includes(keywordToLowerCase)) ||
            (d.askingPrice && d.askingPrice.toString().includes(keywordToLowerCase)) ||
            (d.zoning && d.zoning.toLowerCase().includes(keywordToLowerCase)) ||
            (d.buildingSize && d.buildingSize.toString().includes(keywordToLowerCase)) ||
            (d.landSizeAcres && d.landSizeAcres.toString().includes(keywordToLowerCase)) ||
            (d.usableLand && d.usableLand.toString().includes(keywordToLowerCase)) ||
            (d.OID && d.OID.toLowerCase().includes(keywordToLowerCase)) ||
            (d.dropboxLink && d.dropboxLink.toLowerCase().includes(keywordToLowerCase)) ||
            (d.parcelId && d.parcelId.toLowerCase().includes(keywordToLowerCase)) ||
            (d.address && d.address.toLowerCase().includes(keywordToLowerCase)) ||
            (d.city && d.city.toLowerCase().includes(keywordToLowerCase)) ||
            (d.county && d.county.toLowerCase().includes(keywordToLowerCase)) ||
            (d.state && d.state.toLowerCase().includes(keywordToLowerCase))
        );
    }, [personFilteredDeals, dealFilters.keyword]);
}

export const useMinMaxValue = () => {
    const pipedriveStore = usePipedriveStore();
    return useMemo(() => computed(() => getMinMax(pipedriveStore.deals, deal => deal.value)), []).get();
}

export const useMinMaxBuildingSize = () => {
    const pipedriveStore = usePipedriveStore();
    return useMemo(() => computed(() => getMinMax(pipedriveStore.deals, deal => deal.buildingSize)), []).get();
}

export const useMinMaxLandSize = () => {
    const pipedriveStore = usePipedriveStore();
    return useMemo(() => computed(() => getMinMax(pipedriveStore.deals, deal => deal.landSizeAcres)), []).get();
}